import { Icon, type IconName } from './Icon';

/**
 * Abas com contorno (RNF15 / RNF16).
 *
 * E o padrao visual das secoes do painel: cada aba e uma pastilha com borda;
 * a ativa fica cheia na cor da marca. Sao ABAS DE CONTEUDO (mudam o que esta
 * abaixo na mesma pagina), nao navegacao entre rotas -- para rotas use o menu
 * lateral ou `LinkButton`.
 *
 * Teclado: setas trocam a aba (padrao WAI-ARIA de tabs com ativacao
 * automatica). Em tela estreita a lista rola na horizontal em vez de quebrar
 * em duas linhas, que empurraria o conteudo para baixo a cada troca.
 */

export interface TabItem<T extends string> {
  readonly value: T;
  readonly label: string;
  readonly icon?: IconName;
  /** Contador a direita do rotulo, ja formatado. */
  readonly count?: string;
  readonly disabled?: boolean;
}

interface TabsProps<T extends string> {
  readonly items: readonly TabItem<T>[];
  readonly value: T;
  readonly onChange: (value: T) => void;
  /** Nome acessivel do conjunto de abas. */
  readonly label: string;
  readonly size?: 'sm' | 'md';
  /** Painel controlado, para `aria-controls`. Opcional. */
  readonly panelId?: string;
}

export function Tabs<T extends string>({ items, value, onChange, label, size = 'md', panelId }: TabsProps<T>): JSX.Element {
  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') {
      return;
    }
    event.preventDefault();
    const habilitadas = items.filter((i) => i.disabled !== true);
    const atual = habilitadas.findIndex((i) => i.value === value);
    if (atual < 0 || habilitadas.length === 0) {
      return;
    }
    const passo = event.key === 'ArrowRight' ? 1 : -1;
    const proxima = habilitadas[(atual + passo + habilitadas.length) % habilitadas.length];
    if (proxima !== undefined) {
      onChange(proxima.value);
    }
  };

  const pad = size === 'sm' ? 'h-9 px-3 text-xs' : 'h-11 px-4 text-sm';

  return (
    <div role="tablist" aria-label={label} onKeyDown={onKeyDown} className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
      {items.map((item) => {
        const ativa = item.value === value;
        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={ativa}
            aria-controls={panelId}
            tabIndex={ativa ? 0 : -1}
            disabled={item.disabled}
            onClick={() => onChange(item.value)}
            className={`inline-flex shrink-0 items-center gap-2 rounded-xl border font-medium transition-all duration-150 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 ${pad} ${
              ativa
                ? 'border-brand bg-brand text-white shadow-soft'
                : 'border-line bg-surface text-ink-muted hover:border-brand/60 hover:bg-brand/5 hover:text-ink'
            }`}
          >
            {item.icon === undefined ? null : <Icon name={item.icon} size="sm" />}
            <span>{item.label}</span>
            {item.count === undefined ? null : (
              <span className={`rounded-md px-1.5 py-0.5 text-[11px] tabular-nums ${ativa ? 'bg-white/20 text-white' : 'bg-ink/5 text-ink-subtle'}`}>{item.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
